<script setup>
/**
 * GoalsView
 * Main view for Goals feature with tab navigation
 */
import { ref, computed } from 'vue'
import { storeToRefs } from 'pinia'
import AppButton from '@/ui/components/AppButton.vue'
import GoalForm from './GoalForm.vue'
import GoalList from './GoalList.vue'
import { useAuthStore } from '@/app/store/auth'

const authStore = useAuthStore()

// Tabs
const tabs = [
  { id: 'current', label: 'יעדים נוכחיים', icon: 'target' },
  { id: 'new', label: 'יעד חדש', icon: 'plus', adminOnly: true },
  { id: 'past', label: 'יעדים שעברו', icon: 'history' },
]

const activeTab = ref('current')
const editingGoal = ref(null)

// Check admin status
const { isAdmin } = storeToRefs(authStore)

// Filter tabs based on admin status
const visibleTabs = computed(() => {
  return tabs.filter((tab) => !tab.adminOnly || isAdmin.value)
})

// Handle tab click
function setActiveTab(tabId) {
  activeTab.value = tabId
  // Clear editing goal when switching tabs
  if (tabId !== 'new') {
    editingGoal.value = null
  }
}

// Handle edit goal from list views
function handleEditGoal(goal) {
  editingGoal.value = goal
  activeTab.value = 'new'
}

// Handle form close
function handleFormClose() {
  editingGoal.value = null
  activeTab.value = 'current'
}

// Handle form saved
function handleFormSaved() {
  editingGoal.value = null
  activeTab.value = 'current'
}

// Get icon SVG for tab
function getTabIcon(iconName) {
  switch (iconName) {
    case 'target':
      return `<svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2">
        <circle cx="12" cy="12" r="10"/>
        <circle cx="12" cy="12" r="6"/>
        <circle cx="12" cy="12" r="2"/>
      </svg>`
    case 'plus':
      return `<svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2">
        <line x1="12" y1="5" x2="12" y2="19"/>
        <line x1="5" y1="12" x2="19" y2="12"/>
      </svg>`
    case 'history':
      return `<svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2">
        <circle cx="12" cy="12" r="10"/>
        <polyline points="12 6 12 12 16 14"/>
      </svg>`
    default:
      return ''
  }
}
</script>

<template>
  <div class="goals-container">
    <!-- Header -->
    <div class="view-header">
      <h1 class="view-title">יעדים</h1>
    </div>

    <!-- Tab Navigation -->
    <nav class="tab-nav">
      <button
        v-for="tab in visibleTabs"
        :key="tab.id"
        class="tab-btn"
        :class="{ active: activeTab === tab.id }"
        @click="setActiveTab(tab.id)"
      >
        <span class="tab-icon" v-html="getTabIcon(tab.icon)"></span>
        <span class="tab-label">{{ tab.label }}</span>
      </button>
    </nav>

    <!-- Scrollable Content -->
    <div class="goals-content">
      <!-- Current Goals -->
      <GoalList v-if="activeTab === 'current'" mode="current" @edit-goal="handleEditGoal" />

      <!-- New/Edit Goal Form -->
      <div v-if="activeTab === 'new'" class="form-container">
        <GoalForm :edit-goal="editingGoal" @close="handleFormClose" @saved="handleFormSaved" />
      </div>

      <!-- Past Goals -->
      <GoalList v-if="activeTab === 'past'" mode="past" @edit-goal="handleEditGoal" />
    </div>
  </div>
</template>

<style scoped>
.goals-container {
  display: flex;
  flex-direction: column;
  height: calc(100vh - var(--header-height));
  max-width: 1400px;
  margin: 0 auto;
  padding: var(--spacing-md);
  overflow: hidden;
  box-sizing: border-box;
}

/* Header */
.view-header {
  margin-bottom: var(--spacing-md);
}

.view-title {
  font-size: var(--font-size-2xl);
  font-weight: var(--font-weight-bold);
  color: var(--text-primary);
  margin: 0;
}

/* Tab Navigation */
.tab-nav {
  display: flex;
  gap: var(--spacing-xs);
  padding: var(--spacing-xs);
  background: var(--bg-secondary);
  margin-bottom: var(--spacing-md);
  overflow-x: auto;
  flex-shrink: 0;
}

/* Scrollable Content Area */
.goals-content {
  flex: 1;
  overflow-y: auto;
  min-height: 0;
}

.tab-btn {
  display: flex;
  align-items: center;
  gap: var(--spacing-xs);
  padding: var(--spacing-sm) var(--spacing-md);
  background: transparent;
  border: none;
  border-radius: var(--border-radius);
  color: var(--text-secondary);
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-medium);
  cursor: pointer;
  transition: all var(--transition-fast);
  white-space: nowrap;
}

.tab-btn:hover {
  background: var(--bg-tertiary);
  color: var(--text-primary);
}

.tab-btn.active {
  background: var(--color-primary);
  color: white;
}

.tab-icon {
  display: flex;
  align-items: center;
}

.tab-btn.active .tab-icon :deep(svg) {
  stroke: white;
}

/* Form Container */
.form-container {
  height: 100%;
}

/* Responsive */
@media (max-width: 600px) {
  .goals-container {
    padding: var(--spacing-sm);
  }

  .tab-nav {
    padding: var(--spacing-xs);
  }

  .tab-btn {
    padding: var(--spacing-xs) var(--spacing-sm);
  }

  .tab-label {
    display: none;
  }

  .tab-btn.active .tab-label {
    display: inline;
  }
}
</style>
